/*
 * Vieques Worx strings, English and Puerto Rican Spanish.
 *
 * A separate table from lib/i18n.ts, which is core and whose keys every
 * Studio42 app shares. Adding forty job-board keys there would inherit them
 * into every other project; this file is the application's own, and `tw()`
 * reads the same locale core does so the two can never disagree about which
 * language is on.
 *
 * ── es-PR, not "Spanish" ────────────────────────────────────────────────────
 *
 * Written as it is said on the island. `trabajo` rather than `empleo` for the
 * everyday noun, `chiripa` for gig work — the word people actually use for
 * exactly that kind of work, which a generic translation renders as `trabajo
 * temporal` and loses.
 */
import { getLocale } from '../lib/i18n'

const EN = {
  'w.build.label': 'App information',
  'w.build.version': 'Version',
  'w.build.number': 'build',
  'w.build.live': 'Live',
  'w.build.browser': 'Browser',
  'w.build.unavailable': 'App version unavailable',
  'w.build.unknownEnvironment': 'Environment unavailable',
  'w.guidance.completeProfile': 'Complete profile',
  'w.guidance.missingFields': 'Go to missing fields',
  'w.guidance.backApplication': 'Back to application',
  'w.guidance.backJobs': 'Back to jobs',
  'w.guidance.saveReturn': 'Save and return to application',
  'w.guidance.draftKept': 'Your application draft is kept. Complete your profile, then return.',
  'w.guidance.profileNext': 'Complete your profile, then return to jobs.',
  'w.guidance.cancelApplication': 'Cancel application',
  'w.guidance.reviewBusiness': 'Review business details',
  'w.guidance.backBusiness': 'Back to hiring workspace',
  'w.tagline': 'The island works here.',
  'w.promise': 'Find local work. Hire local people.',
  'w.lookingForWork': "I'm Looking for Work",
  'w.hiring': "I'm Hiring",
  'w.lookingForWorkSub': 'Find local jobs that fit your skills.',
  'w.hiringSub': 'Reach dependable local people.',
  'w.alreadyRegistered': 'Already registered?',
  'w.signIn': 'Sign In',
  'w.register': 'Register',
  'w.notLocked': 'This sets how you use the app. You can add finding or offering a pro later.',
  // The handwriting is part of the picture, so it is part of the description:
  // somebody using a screen reader should get the sentence, not just "an
  // illustration". Translated in the alt text, left in Spanish in the art.
  'w.splash.illoAlt':
    'Four neighbours looking out over the bay at Vieques, hand-lettered: "Nuestra gente. Un mejor mañana." — Our people. A better tomorrow.',

  'w.tab.jobs': 'Jobs',
  'w.tab.saved': 'Saved',
  'w.tab.applications': 'Applications',
  'w.tab.messages': 'Messages',
  'w.tab.profile': 'Profile',
  'w.tab.feedback': 'Feedback',
  'w.fb.title': 'Tell us what you think',
  'w.fb.hint': 'An idea, something that is not working, or something you like. A person reads every one.',
  'w.fb.idea': 'An idea',
  'w.fb.problem': 'Something is wrong',
  'w.fb.love': 'I like this',
  'w.fb.placeholder': 'Write it here',
  'w.fb.send': 'Send feedback',
  'w.cheer.feedback': 'Thank you!',
  'w.cheer.feedbackLine': 'We read every message.',
  'w.tab.dashboard': 'Dashboard',
  'w.tab.postings': 'Jobs',
  'w.tab.applicants': 'Applicants',
  'w.tab.business': 'Business',

  'w.profile.title': 'Your profile',
  'w.profile.intro': 'Fill this in once. Every job you apply to gets this — no forms.',
  'w.profile.headline': 'One line about you',
  'w.profile.headlinePlaceholder': 'Line cook, 6 years, Esperanza',
  'w.profile.summary': 'Experience',
  'w.profile.availability': 'When can you work?',
  'w.profile.availabilityPlaceholder': 'Mornings until 2, any hours in low season',
  'w.profile.categories': 'What kind of work?',
  'w.profile.skills': 'Skills',
  'w.profile.skillsHint': 'Comma separated',
  'w.profile.arrangements': 'Type of work',
  'w.profile.transport': 'Getting to work',
  'w.profile.pay': 'Preferred pay',
  'w.profile.resume': 'Résumé (optional)',
  'w.profile.resumeHint': 'You do not need one to apply.',
  'w.profile.discoverable': 'Let employers find me',
  'w.profile.discoverableHint': 'Applying always shows your profile to that employer.',
  'w.profile.save': 'Save profile',
  'w.profile.saved': 'Saved.',
  'w.profile.incomplete': 'Add a headline, your availability and one category to start applying.',

  'w.nav.back': 'Back',
  'w.jobs.search': 'Search jobs',
  'w.jobs.loading': 'Loading jobs…',
  'w.jobs.loadingDetail': 'Loading job details…',
  'w.jobs.loadError': 'We could not load the jobs. Please try again.',
  'w.jobs.retry': 'Try again',
  'w.jobs.more': 'Load more jobs',
  'w.jobs.description': 'About this job',
  'w.jobs.none': 'No jobs match that yet.',
  'w.jobs.noneAtAll': 'No jobs posted yet. Check back soon.',
  'w.cheer.applied': 'Application sent!',
  'w.cheer.appliedLine': 'Well done. The business can see it now.',
  'w.cheer.published': 'Your job is live!',
  'w.cheer.publishedLine': 'People on Vieques can see it and apply.',
  'w.cheer.registered': 'Business registered!',
  'w.cheer.registeredLine': 'We will verify it soon. Then you can post jobs.',
  'w.cheer.hired': 'You hired someone!',
  'w.cheer.hiredLine': 'Congratulations on your new team member.',
  'w.sound.on': 'Sound on',
  'w.sound.off': 'Sound off',
  'w.browse.free': 'Browsing is free. You only need an account to apply.',
  'w.browse.join': 'Create an account to apply',
  'w.jobs.all': 'All work',
  'w.jobs.apply': 'Apply now',
  'w.jobs.applied': 'Applied',
  'w.jobs.save': 'Save',
  'w.jobs.unsave': 'Saved',
  'w.jobs.noPay': 'Pay not stated',
  'w.jobs.closes': 'Closes',
  'w.jobs.starts': 'Starts',
  'w.jobs.requirements': 'Requirements',
  'w.jobs.about': 'About the employer',
  'w.jobs.verified': 'Verified employer',
  'w.jobs.report': 'Report this posting',

  'w.apply.title': 'Send your profile',
  'w.apply.confirm': 'Confirm when you can start',
  'w.apply.note': 'Anything to add? (optional)',
  'w.apply.send': 'Send application',
  'w.apply.sent': 'Application sent.',

  'w.saved.none': 'Nothing saved yet. Tap Save on a job to keep it here.',
  'w.biz.screenTitle': 'Business',
  'w.biz.viewPublic': 'View Public Profile',
  'w.biz.edit': 'Edit Business',
  'w.biz.verified2': 'Verified Employer',
  'w.biz.pendingReview': 'Waiting to be verified',
  'w.biz.details': 'Business details',
  'w.biz.account': 'Account & team',
  'w.biz.contactRow': 'Contact information',
  'w.biz.contactRowEmpty': 'Add a phone number and an email',
  'w.biz.aboutRow': 'Business description',
  'w.biz.aboutRowEmpty': 'Say what the place is',
  'w.biz.taglineRow': 'Your one line',
  'w.biz.taglineRowEmpty': 'The line on every job you post',
  'w.biz.teamRow': 'Team access',
  'w.biz.teamSoon': 'Inviting somebody else to post for this business is not built yet. Ask us through Get Help and we will add them by hand.',
  'w.biz.admin1': 'administrator',
  'w.biz.adminMany': 'administrators',
  'w.biz.accountRow': 'Passkeys & notifications',
  'w.biz.accountRowSub': 'Devices, alerts and how you sign in',
  'w.biz.switchRow': 'Switch role',
  'w.biz.switchRowSub': 'Use Vieques Worx as a worker',
  'w.biz.help': 'Get Help',
  'w.biz.helpTitle': 'What do you need?',
  'w.biz.helpHint': 'This goes straight to the people who run Vieques Worx. Tell us what happened and we will answer here.',
  'w.biz.helpSend': 'Send',
  'w.biz.helpSent': 'Sent. We will get back to you.',
  'w.biz.location': 'Town',
  'w.biz.save': 'Save',
  'w.biz.saved': 'Saved.',
  'w.biz.publicTitle': 'What applicants see',
  'w.biz.openJobs': 'Open jobs',
  'w.apps.title': 'My Applications',
  'w.apps.active': 'Active',
  'w.apps.past': 'Past',
  'w.apps.banner': 'Employers will message you here when your application moves forward.',
  'w.apps.submitted': 'Submitted',
  'w.apps.noneActive': 'Nothing open right now. Apply to a job and it appears here.',
  'w.apps.nonePast': 'Nothing finished yet.',
  /* What actually happened last, in the worker's words. The status pill names
     the STAGE; this line names the EVENT, which is the thing somebody opens
     this screen to find out. */
  'w.apps.ev.applied': 'Waiting to be opened',
  'w.apps.ev.viewed': 'Application viewed',
  'w.apps.ev.contacted': 'Employer sent you a message',
  'w.apps.ev.interview': 'Interview requested',
  'w.apps.ev.hired': 'You got the job',
  'w.apps.ev.not_selected': 'They went with someone else',
  'w.apps.ev.withdrawn': 'You withdrew this',
  'w.apps.openConversation': 'Open Conversation',
  'w.apps.viewUpdate': 'View Update',
  'w.apps.viewApplication': 'View Application',
  'w.saved.title': 'Saved Jobs',
  /* Two whole sentences rather than a count and a fragment: Spanish agrees the
     participle with the noun, so "guardado" and "guardados" cannot be built by
     sticking a plural s on the end of a shared tail. */
  'w.saved.one': 'job saved for later.',
  'w.saved.many': 'jobs saved for later.',
  'w.saved.search': 'Search saved jobs',
  'w.saved.hint': 'Tap the bookmark to remove a saved job.',
  'w.saved.noMatch': 'Nothing saved matches that.',
  /* The pay unit as a suffix, for a card where the figure has half a row.
     "$14–$16 per hour" wrapped; "$14–$16/hr" does not, and nobody has ever
     misread it. The long form stays on the posting itself. */
  'w.payShort.hour': '/hr',
  'w.payShort.day': '/day',
  'w.payShort.salary': '/yr',
  'w.payShort.contract': '/job',
  'w.payShort.tipped': '+tips',
  'w.jobs.closesSoon': 'Closes soon',
  'w.jobs.viewJob': 'View Job',
  'w.applications.none': 'No applications yet.',
  'w.applications.withdraw': 'Withdraw',
  'w.applications.withdrawn': 'Withdrawn',

  'w.status.applied': 'Applied',
  'w.status.viewed': 'Viewed',
  'w.status.contacted': 'Contacted',
  'w.status.interview': 'Interview',
  'w.status.hired': 'Hired',
  'w.status.not_selected': 'Not selected',
  'w.status.withdrawn': 'Withdrawn',

  'w.mail.title': 'Messages',
  'w.mail.none': 'No messages. A conversation starts when you apply.',
  'w.mail.reply': 'Reply',
  'w.mail.send': 'Send',
  'w.mail.placeholder': 'Write a reply…',
  'w.mail.block': 'End this conversation',
  'w.mail.blocked': 'This conversation has ended.',
  'w.mail.unblock': 'Reopen',
  'w.mail.relayed': 'Also sent by WhatsApp',
  'w.mail.empty': 'Nothing here yet. Say hello.',

  'w.biz.register': 'Register your business',
  'w.biz.intro': 'Workers see verified businesses first. Tell us who you are and we will check it before you post.',
  'w.biz.secBusiness': 'Your business',
  'w.biz.secReach': 'How people reach you',
  'w.biz.secProof': 'Show us it is real',
  'w.biz.name': 'Business name',
  'w.biz.contact': 'Contact name',
  'w.biz.category': 'What kind of business?',
  'w.biz.about': 'About the business',
  'w.biz.proof': 'How can we verify you?',
  'w.biz.proofHint': 'A merchant number, a licence, an address, or someone who will vouch for you.',
  'w.biz.submit': 'Register',
  'w.biz.pending': 'Waiting to be verified. You can save drafts now and publish after approval.',
  'w.biz.rejected': 'We need more information before your business can post.',
  'w.biz.suspended': 'This business is suspended.',
  'w.biz.verified': 'Verified',

  'w.post.new': 'New job post',
  'w.post.title': 'Job title',
  'w.post.description': 'Description',
  'w.post.schedule': 'Schedule',
  'w.post.location': 'Where is the work?',
  'w.post.requirements': 'Requirements',
  'w.post.starts': 'Start date',
  'w.post.closes': 'Application deadline (optional)',
  'w.post.save': 'Save draft',
  'w.post.saveAndPublish': 'Save & publish',
  'w.post.saveChanges': 'Save changes',
  'w.post.publish': 'Publish',
  'w.post.pause': 'Pause',
  'w.post.close': 'Close',
  'w.post.fill': 'Mark filled',
  'w.post.preview': 'Preview',
  'w.post.none': 'No job posts yet.',

  'w.applicants.none': 'No applicants yet.',
  'w.applicants.new': 'new',
  'w.applicants.message': 'Message',
  /* A posting's own state, in the employer's words. The DB calls it
     `published`; an employer calls it live. */
  'w.jobstatus.draft': 'Draft',
  'w.jobstatus.published': 'Active',
  'w.jobstatus.paused': 'Paused',
  'w.jobstatus.filled': 'Filled',
  'w.jobstatus.closed': 'Closed',
  'w.jobstatus.removed': 'Removed',
  /* Apple's rule 1.2 asks for a way to flag content wherever it appears. The
     wording is deliberately plain: somebody reporting a scam is not in the
     mood to decode a euphemism. */
  'w.report.open': 'Report',
  'w.report.job': 'Report this job',
  'w.report.person': 'Report this person',
  'w.report.convo': 'Report this conversation',
  'w.report.title': 'Report this',
  'w.report.hint': 'An administrator reads every report. Say what is wrong and we will look at it.',
  'w.report.reason.scam': 'It is a scam',
  'w.report.reason.not_real': 'It is not real',
  'w.report.reason.discrimination': 'Discrimination',
  'w.report.reason.harassment': 'Harassment',
  'w.report.reason.spam': 'Spam',
  'w.report.reason.other': 'Something else',
  'w.report.detail': 'What happened? (optional)',
  'w.report.send': 'Send report',
  'w.report.sent': 'Sent. An administrator will look at this.',
  'w.mail.screenTitle': 'Messages',
  'w.mail.search': 'Search conversations',
  'w.mail.all': 'All',
  'w.mail.yesterday': 'Yesterday',
  'w.mail.unread': 'Unread',
  'w.mail.applicantConvos': 'Applicant conversations',
  'w.mail.employerConvos': 'Your conversations',
  'w.mail.hint': 'Messages stay connected to each application.',
  'w.mail.noMatch': 'No conversation matches that.',
  'w.mail.allRead': 'Nothing unread.',
  'w.post.screenTitle': 'Jobs',
  'w.post.active': 'Active',
  'w.post.drafts': 'Drafts',
  'w.post.closedTab': 'Closed',
  'w.post.search': 'Search your jobs',
  'w.post.noneHere': 'Nothing in here.',
  'w.post.viewApplicants': 'View Applicants',
  'w.post.finish': 'Finish & Publish',
  'w.post.manage': 'Manage this posting',
  'w.post.edit': 'Edit',
  'w.post.newApplicants': 'new',
  'w.ap.title': 'Applicants',
  'w.ap.one': 'applicant',
  'w.ap.many': 'applicants',
  'w.ap.new': 'New',
  'w.ap.reviewed': 'Reviewed',
  'w.ap.interview': 'Interview',
  'w.ap.decided': 'Decided',
  'w.ap.newest': 'Newest first',
  'w.ap.oldest': 'Oldest first',
  'w.ap.filter': 'Filter',
  'w.ap.greatOnly': 'Great matches only',
  'w.ap.review': 'Review Application',
  /* The employer's half of the same badge the worker sees on a posting. */
  'w.ap.greatMatch': 'Great match',
  'w.ap.pickJob': 'Choose a posting',
  'w.ap.noneHere': 'Nobody in this pile.',
  'w.ap.noJobs': 'Post a job and applicants land here.',
  'w.ap.note': 'What they said',
  'w.ap.move': 'Move to',
  'w.ap.applied': 'Applied',

  'w.pay.hour': 'per hour',
  'w.pay.salary': 'per year',
  'w.pay.day': 'per day',
  'w.pay.contract': 'per contract',
  'w.pay.tipped': 'plus tips',

  'w.arr.full_time': 'Full time',
  'w.arr.part_time': 'Part time',
  'w.arr.temporary': 'Temporary',
  'w.arr.seasonal': 'Seasonal',
  'w.arr.gig': 'Gig',

  'w.dash.heading': 'Let’s build your team',
  'w.dash.post': 'Post a Job',
  'w.dash.activeJobs': 'Active Jobs',
  'w.dash.newApplicants': 'New Applicants',
  'w.dash.unread': 'Unread Messages',
  'w.dash.openJobs': 'Your open jobs',
  'w.dash.viewAllJobs': 'View all jobs',
  'w.dash.viewApplicants': 'View Applicants',
  'w.dash.applicants': 'applicants',
  'w.dash.recent': 'Recent applicants',
  'w.dash.active': 'Active',
  'w.dash.new': 'New',
  'w.dash.posted': 'Posted',
  'w.dash.notPosted': 'Not published yet',
  'w.feed.greeting.morning': 'Good morning',
  'w.feed.greeting.afternoon': 'Good afternoon',
  'w.feed.greeting.evening': 'Good evening',
  'w.feed.heading': 'Find work on Vieques',
  'w.feed.search': 'Search jobs or skills',
  'w.feed.match': 'Great match for you',
  'w.feed.why.category': 'The kind of work you chose',
  'w.feed.why.arrangement': 'The hours you want',
  'w.feed.why.pay': 'In your pay range',
  'w.feed.noProfile': 'Fill in your profile and we will point out the jobs that fit.',
  'w.feed.viewJob': 'View Job',
  'w.feed.more': 'More',
  'w.jobs.interested': 'Interested in this job?',
  'w.nav.jobs': 'Jobs',
  'w.nav.services': 'Services',
  'w.nav.activity': 'My activity',
  'w.nav.main': 'Main',
  'w.nav.betaPreview': 'Beta preview',
  'w.footer.tagline': 'Local opportunities. Island connections.',
  'w.update.title': 'Update required',
  'w.update.body': 'This version of Vieques Worx is out of date and can no longer run. Update the app, then open it again.',
  'w.update.appStore': 'Open the App Store',
  'w.splash.getOnPlay': 'Get Vieques Worx on Google Play',
  'w.update.testFlight': 'Open TestFlight',
  'w.pro.title': 'Need a pro?',
  'w.pro.hint': 'Plumbers, electricians, drivers and more, from people on the island.',
  'w.pro.open': 'Find a pro',
  'w.pro.back': 'Back to my account',
  'w.tr.fromSpanish': 'Translated from Spanish',
  'w.tr.fromEnglish': 'Translated from English',
  'w.tr.showOriginal': 'Show original',
  'w.tr.showTranslation': 'Show translation',
  'w.role.addTitle': 'Use this account another way',
  'w.role.addHint': 'Most people need one. If you also do the other things, add it here and a switch will appear at the top.',
  'w.role.add.worker': 'Also look for work',
  'w.role.add.employer': 'Also hire people',
  'w.role.add.pro': 'Also find or offer a pro',
  'w.role.pick': 'What brings you here?',
  'w.switch.working': 'Looking for work',
  'w.switch.hiring': 'Hiring',
  'w.switch.toHiring': 'Switch to hiring',
  'w.switch.toWorking': 'Switch to looking',
  'w.switch.splash': 'Splash',
  'w.switch.back': 'Back to the app',
  'w.feed.subtitle': 'Find work close to home.',
  'w.feed.newToday': 'new jobs today',
  'w.feed.newTodayOne': 'new job today',
  'w.feed.recommended': 'Recommended for you',
  'w.feed.seeAll': 'See all',
  'w.feed.allJobs': 'All jobs on Vieques',
  'w.feed.allShort': 'All',
  'w.feed.done': 'Done',
  'w.feed.posted': 'Posted',
  'w.tab.home': 'Home',
  'w.admin.section': 'Admin',
  'w.bell.label': 'Notifications',
  'w.biz.tagline': 'Your one line',
  'w.biz.taglineHint': 'Shown on every job you post. One short line.',

  'w.delete.open': 'Delete your account',
  'w.delete.title': 'Delete your account',
  'w.delete.what': 'Your account, your passkeys, your profile, your applications, your messages and your saved jobs. All of it, permanently.',
  'w.delete.orphanWarning': 'You are the only person on these businesses:',
  'w.delete.livePostings': 'live posting(s)',
  'w.delete.orphanOutcome': 'Their published jobs will be paused and an administrator will be asked to hand the business to somebody who works there.',
  'w.delete.confirmLabel': 'Type your username to confirm',
  'w.delete.confirm': 'Delete my account permanently',
  'w.delete.permanent': 'There is no undo and no grace period.',

  'w.transport.own': 'I have my own transport',
  'w.transport.shared': 'I can usually get a ride',
  'w.transport.public': 'Público or walking',
  'w.transport.none': 'Transport is a problem',
  /*
   * The same four answers, short enough for a button.
   *
   * The long forms stay: they are what an employer reads on an application,
   * where the sentence is the point. These are what the person picking reads,
   * where four full sentences stacked in a column is a wall. Neither is a
   * mechanical shortening of the other — both are written.
   */
  'w.transport.short.own': 'My own',
  'w.transport.short.shared': 'I get a ride',
  'w.transport.short.public': 'Público or walk',
  'w.transport.short.none': "It's a problem",

  /* The profile, in three parts. See Profile.tsx for why it is grouped. */
  'w.profile.aboutYou': 'About you',
  'w.profile.theWork': 'The work you want',
  'w.profile.practical': 'The practical part',
  'w.profile.payFrom': 'From',
  'w.profile.payTo': 'To',
  'w.profile.payPer': 'Per',
  'w.profile.categoriesHint': 'Pick every kind you would take — this is what matches you to a job.',
  "w.biz.phone": "Phone",
  "w.biz.email": "Email",
  "w.biz.taglineExample": "Good food. Brighter days.",
  "w.post.pay": "Pay",
  "w.ap.applicant": "Applicant",
  "w.ap.applicants": "Applicants",
  "w.unread": "unread",
  "w.error.save": "That did not save.",
  "w.error.send": "That did not send.",
  "w.error.work": "That did not work.",
  "w.error.jobLoad": "That posting could not be loaded.",
  "w.error.conversationLoad": "That conversation could not be loaded.",
  "w.demo.title": "Demonstration data",
  "w.demo.practice": "These are sample jobs, not real openings. Applications and messages are for testing only.",
  "w.demo.history": "This is a sample business and posting, not a real opening. Your existing application history is preserved.",
  "w.demo.business": "Sample business",
  "w.demo.badge": "Demo",
  "w.demo.apply": "Try a demo application",
} as const

export type WorxKey = keyof typeof EN

const ES: Record<WorxKey, string> = {
  'w.build.label': 'Información de la app',
  'w.build.version': 'Versión',
  'w.build.number': 'compilación',
  'w.build.live': 'Producción',
  'w.build.browser': 'Navegador',
  'w.build.unavailable': 'Versión de la app no disponible',
  'w.build.unknownEnvironment': 'Entorno no disponible',
  'w.guidance.completeProfile': 'Completar perfil',
  'w.guidance.missingFields': 'Ir a los campos que faltan',
  'w.guidance.backApplication': 'Volver a la solicitud',
  'w.guidance.backJobs': 'Volver a los trabajos',
  'w.guidance.saveReturn': 'Guardar y volver a la solicitud',
  'w.guidance.draftKept': 'Tu borrador de solicitud se conserva. Completa tu perfil y luego vuelve.',
  'w.guidance.profileNext': 'Completa tu perfil y luego vuelve a los trabajos.',
  'w.guidance.cancelApplication': 'Cancelar solicitud',
  'w.guidance.reviewBusiness': 'Revisar datos del negocio',
  'w.guidance.backBusiness': 'Volver al área de contratación',
  'w.tagline': 'Aquí trabaja la isla.',
  'w.promise': 'Encuentra trabajo local. Emplea gente local.',
  'w.lookingForWork': 'Busco trabajo',
  'w.hiring': 'Estoy empleando',
  'w.lookingForWorkSub': 'Trabajos locales para tus destrezas.',
  'w.hiringSub': 'Llega a gente confiable de aquí.',
  'w.alreadyRegistered': '¿Ya te registraste?',
  'w.signIn': 'Entrar',
  'w.register': 'Registrarme',
  'w.notLocked': 'Esto define cómo usas la app. Después puedes añadir buscar u ofrecer un profesional.',
  'w.splash.illoAlt':
    'Cuatro vecinos mirando la bahía de Vieques, con letra a mano: "Nuestra gente. Un mejor mañana."',

  'w.tab.jobs': 'Trabajos',
  'w.tab.saved': 'Guardados',
  'w.tab.applications': 'Solicitudes',
  'w.tab.messages': 'Mensajes',
  'w.tab.profile': 'Perfil',
  'w.tab.feedback': 'Opiniones',
  'w.fb.title': 'Cuéntanos qué piensas',
  'w.fb.hint': 'Una idea, algo que no funciona o algo que te gusta. Una persona lee cada mensaje.',
  'w.fb.idea': 'Una idea',
  'w.fb.problem': 'Algo está mal',
  'w.fb.love': 'Me gusta',
  'w.fb.placeholder': 'Escríbelo aquí',
  'w.fb.send': 'Enviar opinión',
  'w.cheer.feedback': '¡Gracias!',
  'w.cheer.feedbackLine': 'Leemos cada mensaje.',
  'w.tab.dashboard': 'Resumen',
  'w.tab.postings': 'Puestos',
  'w.tab.applicants': 'Solicitantes',
  'w.tab.business': 'Negocio',

  'w.profile.title': 'Tu perfil',
  'w.profile.intro': 'Llénalo una vez. Cada trabajo al que apliques recibe esto — sin formularios.',
  'w.profile.headline': 'Una línea sobre ti',
  'w.profile.headlinePlaceholder': 'Cocinero, 6 años, Esperanza',
  'w.profile.summary': 'Experiencia',
  'w.profile.availability': '¿Cuándo puedes trabajar?',
  'w.profile.availabilityPlaceholder': 'Mañanas hasta las 2, cualquier hora en temporada baja',
  'w.profile.categories': '¿Qué tipo de trabajo?',
  'w.profile.skills': 'Destrezas',
  'w.profile.skillsHint': 'Separadas por comas',
  'w.profile.arrangements': 'Tipo de empleo',
  'w.profile.transport': 'Para llegar al trabajo',
  'w.profile.pay': 'Paga que prefieres',
  'w.profile.resume': 'Résumé (opcional)',
  'w.profile.resumeHint': 'No necesitas uno para aplicar.',
  'w.profile.discoverable': 'Que los patronos me encuentren',
  'w.profile.discoverableHint': 'Aplicar siempre le muestra tu perfil a ese patrono.',
  'w.profile.save': 'Guardar perfil',
  'w.profile.saved': 'Guardado.',
  'w.profile.incomplete': 'Añade una línea sobre ti, tu disponibilidad y una categoría para empezar a aplicar.',

  'w.nav.back': 'Volver',
  'w.jobs.search': 'Buscar trabajos',
  'w.jobs.loading': 'Cargando trabajos…',
  'w.jobs.loadingDetail': 'Cargando detalles del trabajo…',
  'w.jobs.loadError': 'No pudimos cargar los trabajos. Inténtalo de nuevo.',
  'w.jobs.retry': 'Intentar de nuevo',
  'w.jobs.more': 'Ver más trabajos',
  'w.jobs.description': 'Sobre este trabajo',
  'w.jobs.none': 'Ningún trabajo coincide todavía.',
  'w.jobs.noneAtAll': 'Todavía no hay trabajos publicados. Vuelve pronto.',
  'w.cheer.applied': '¡Solicitud enviada!',
  'w.cheer.appliedLine': '¡Bien hecho! El negocio ya puede verla.',
  'w.cheer.published': '¡Tu trabajo está publicado!',
  'w.cheer.publishedLine': 'La gente de Vieques ya puede verlo y solicitar.',
  'w.cheer.registered': '¡Negocio registrado!',
  'w.cheer.registeredLine': 'Lo verificaremos pronto. Luego podrás publicar trabajos.',
  'w.cheer.hired': '¡Contrataste a alguien!',
  'w.cheer.hiredLine': 'Felicidades por tu nuevo integrante del equipo.',
  'w.sound.on': 'Sonido activado',
  'w.sound.off': 'Sonido apagado',
  'w.browse.free': 'Ver los trabajos es gratis. Solo necesitas una cuenta para solicitar.',
  'w.browse.join': 'Crear una cuenta para solicitar',
  'w.jobs.all': 'Todo tipo de trabajo',
  'w.jobs.apply': 'Aplicar ahora',
  'w.jobs.applied': 'Aplicaste',
  'w.jobs.save': 'Guardar',
  'w.jobs.unsave': 'Guardado',
  'w.jobs.noPay': 'No indica la paga',
  'w.jobs.closes': 'Cierra',
  'w.jobs.starts': 'Empieza',
  'w.jobs.requirements': 'Requisitos',
  'w.jobs.about': 'Sobre el patrono',
  'w.jobs.verified': 'Patrono verificado',
  'w.jobs.report': 'Reportar este puesto',

  'w.apply.title': 'Envía tu perfil',
  'w.apply.confirm': 'Confirma cuándo puedes empezar',
  'w.apply.note': '¿Algo que añadir? (opcional)',
  'w.apply.send': 'Enviar solicitud',
  'w.apply.sent': 'Solicitud enviada.',

  'w.saved.none': 'Nada guardado todavía. Toca Guardar en un trabajo para tenerlo aquí.',
  'w.biz.screenTitle': 'Negocio',
  'w.biz.viewPublic': 'Ver perfil público',
  'w.biz.edit': 'Editar negocio',
  'w.biz.verified2': 'Patrono verificado',
  'w.biz.pendingReview': 'Esperando verificación',
  'w.biz.details': 'Datos del negocio',
  'w.biz.account': 'Cuenta y equipo',
  'w.biz.contactRow': 'Información de contacto',
  'w.biz.contactRowEmpty': 'Añade un teléfono y un email',
  'w.biz.aboutRow': 'Descripción del negocio',
  'w.biz.aboutRowEmpty': 'Di qué es el sitio',
  'w.biz.taglineRow': 'Tu frase',
  'w.biz.taglineRowEmpty': 'La línea en cada trabajo que publiques',
  'w.biz.teamRow': 'Acceso del equipo',
  'w.biz.teamSoon': 'Invitar a otra persona a publicar por este negocio todavía no está hecho. Pídelo en Buscar ayuda y la añadimos a mano.',
  'w.biz.admin1': 'administrador',
  'w.biz.adminMany': 'administradores',
  'w.biz.accountRow': 'Passkeys y notificaciones',
  'w.biz.accountRowSub': 'Dispositivos, avisos y cómo entras',
  'w.biz.switchRow': 'Cambiar de lado',
  'w.biz.switchRowSub': 'Usa Vieques Worx como trabajador',
  'w.biz.help': 'Buscar ayuda',
  'w.biz.helpTitle': '¿Qué necesitas?',
  'w.biz.helpHint': 'Esto llega directo a quienes manejan Vieques Worx. Cuéntanos qué pasó y te contestamos aquí.',
  'w.biz.helpSend': 'Enviar',
  'w.biz.helpSent': 'Enviado. Te contestamos pronto.',
  'w.biz.location': 'Pueblo',
  'w.biz.save': 'Guardar',
  'w.biz.saved': 'Guardado.',
  'w.biz.publicTitle': 'Lo que ven los solicitantes',
  'w.biz.openJobs': 'Trabajos abiertos',
  'w.apps.title': 'Mis solicitudes',
  'w.apps.active': 'Activas',
  'w.apps.past': 'Pasadas',
  'w.apps.banner': 'Los patronos te escriben aquí cuando tu solicitud avanza.',
  'w.apps.submitted': 'Enviada',
  'w.apps.noneActive': 'Nada abierto ahora mismo. Aplica a un trabajo y aparece aquí.',
  'w.apps.nonePast': 'Nada terminado todavía.',
  'w.apps.ev.applied': 'Esperando a que la abran',
  'w.apps.ev.viewed': 'Vieron tu solicitud',
  'w.apps.ev.contacted': 'El patrono te escribió',
  'w.apps.ev.interview': 'Te pidieron entrevista',
  'w.apps.ev.hired': 'Conseguiste el trabajo',
  'w.apps.ev.not_selected': 'Escogieron a otra persona',
  'w.apps.ev.withdrawn': 'Retiraste esta solicitud',
  'w.apps.openConversation': 'Abrir conversación',
  'w.apps.viewUpdate': 'Ver novedad',
  'w.apps.viewApplication': 'Ver solicitud',
  'w.saved.title': 'Trabajos guardados',
  'w.saved.one': 'trabajo guardado para después.',
  'w.saved.many': 'trabajos guardados para después.',
  'w.saved.search': 'Busca en tus guardados',
  'w.saved.hint': 'Toca el marcador para quitar un trabajo guardado.',
  'w.saved.noMatch': 'Nada de lo guardado coincide con eso.',
  'w.payShort.hour': '/hr',
  'w.payShort.day': '/día',
  'w.payShort.salary': '/año',
  'w.payShort.contract': '/trabajo',
  'w.payShort.tipped': '+propinas',
  'w.jobs.closesSoon': 'Cierra pronto',
  'w.jobs.viewJob': 'Ver trabajo',
  'w.applications.none': 'Todavía no has aplicado a nada.',
  'w.applications.withdraw': 'Retirar',
  'w.applications.withdrawn': 'Retirada',

  'w.status.applied': 'Aplicaste',
  'w.status.viewed': 'La vieron',
  'w.status.contacted': 'Te contactaron',
  'w.status.interview': 'Entrevista',
  'w.status.hired': 'Te emplearon',
  'w.status.not_selected': 'No seleccionado',
  'w.status.withdrawn': 'Retirada',

  'w.mail.title': 'Mensajes',
  'w.mail.none': 'No hay mensajes. La conversación empieza cuando aplicas.',
  'w.mail.reply': 'Responder',
  'w.mail.send': 'Enviar',
  'w.mail.placeholder': 'Escribe una respuesta…',
  'w.mail.block': 'Terminar esta conversación',
  'w.mail.blocked': 'Esta conversación terminó.',
  'w.mail.unblock': 'Reabrir',
  'w.mail.relayed': 'También enviado por WhatsApp',
  'w.mail.empty': 'Todavía no hay nada. Saluda.',

  'w.biz.register': 'Registra tu negocio',
  'w.biz.intro': 'Los trabajadores ven primero los negocios verificados. Dinos quién eres y lo revisamos antes de que publiques.',
  'w.biz.secBusiness': 'Tu negocio',
  'w.biz.secReach': 'Cómo te contactan',
  'w.biz.secProof': 'Demuestra que es real',
  'w.biz.name': 'Nombre del negocio',
  'w.biz.contact': 'Persona de contacto',
  'w.biz.category': '¿Qué tipo de negocio?',
  'w.biz.about': 'Sobre el negocio',
  'w.biz.proof': '¿Cómo te podemos verificar?',
  'w.biz.proofHint': 'Un número de comerciante, una licencia, una dirección, o alguien que responda por ti.',
  'w.biz.submit': 'Registrar',
  'w.biz.pending': 'Esperando verificación. Puedes guardar borradores ahora y publicar después de la aprobación.',
  'w.biz.rejected': 'Necesitamos más información antes de que tu negocio pueda publicar.',
  'w.biz.suspended': 'Este negocio está suspendido.',
  'w.biz.verified': 'Verificado',

  'w.post.new': 'Nuevo puesto',
  'w.post.title': 'Título del puesto',
  'w.post.description': 'Descripción',
  'w.post.schedule': 'Horario',
  'w.post.location': '¿Dónde es el trabajo?',
  'w.post.requirements': 'Requisitos',
  'w.post.starts': 'Fecha de comienzo',
  'w.post.closes': 'Fecha límite para aplicar (opcional)',
  'w.post.save': 'Guardar borrador',
  'w.post.saveAndPublish': 'Guardar y publicar',
  'w.post.saveChanges': 'Guardar cambios',
  'w.post.publish': 'Publicar',
  'w.post.pause': 'Pausar',
  'w.post.close': 'Cerrar',
  'w.post.fill': 'Marcar ocupado',
  'w.post.preview': 'Vista previa',
  'w.post.none': 'Todavía no hay puestos.',

  'w.applicants.none': 'Todavía no hay solicitantes.',
  'w.applicants.new': 'nuevo',
  'w.applicants.message': 'Mensaje',
  'w.jobstatus.draft': 'Borrador',
  'w.jobstatus.published': 'Activo',
  'w.jobstatus.paused': 'En pausa',
  'w.jobstatus.filled': 'Ocupado',
  'w.jobstatus.closed': 'Cerrado',
  'w.jobstatus.removed': 'Retirado',
  'w.report.open': 'Reportar',
  'w.report.job': 'Reportar este trabajo',
  'w.report.person': 'Reportar a esta persona',
  'w.report.convo': 'Reportar esta conversación',
  'w.report.title': 'Reportar esto',
  'w.report.hint': 'Un administrador lee cada reporte. Dinos qué está mal y lo revisamos.',
  'w.report.reason.scam': 'Es una estafa',
  'w.report.reason.not_real': 'No es real',
  'w.report.reason.discrimination': 'Discriminación',
  'w.report.reason.harassment': 'Acoso',
  'w.report.reason.spam': 'Spam',
  'w.report.reason.other': 'Otra cosa',
  'w.report.detail': '¿Qué pasó? (opcional)',
  'w.report.send': 'Enviar reporte',
  'w.report.sent': 'Enviado. Un administrador lo va a revisar.',
  'w.mail.screenTitle': 'Mensajes',
  'w.mail.search': 'Busca conversaciones',
  'w.mail.all': 'Todas',
  'w.mail.yesterday': 'Ayer',
  'w.mail.unread': 'Sin leer',
  'w.mail.applicantConvos': 'Conversaciones con solicitantes',
  'w.mail.employerConvos': 'Tus conversaciones',
  'w.mail.hint': 'Cada conversación queda pegada a su solicitud.',
  'w.mail.noMatch': 'Ninguna conversación coincide.',
  'w.mail.allRead': 'Nada sin leer.',
  'w.post.screenTitle': 'Trabajos',
  'w.post.active': 'Activos',
  'w.post.drafts': 'Borradores',
  'w.post.closedTab': 'Cerrados',
  'w.post.search': 'Busca en tus trabajos',
  'w.post.noneHere': 'Nada aquí.',
  'w.post.viewApplicants': 'Ver solicitantes',
  'w.post.finish': 'Terminar y publicar',
  'w.post.manage': 'Manejar esta publicación',
  'w.post.edit': 'Editar',
  'w.post.newApplicants': 'nuevas',
  'w.ap.title': 'Solicitantes',
  'w.ap.one': 'solicitante',
  'w.ap.many': 'solicitantes',
  'w.ap.new': 'Nuevas',
  'w.ap.reviewed': 'Vistas',
  'w.ap.interview': 'Entrevista',
  'w.ap.decided': 'Decididas',
  'w.ap.newest': 'Más recientes',
  'w.ap.oldest': 'Más antiguas',
  'w.ap.filter': 'Filtrar',
  'w.ap.greatOnly': 'Solo buenos matches',
  'w.ap.review': 'Ver solicitud',
  'w.ap.greatMatch': 'Buen match',
  'w.ap.pickJob': 'Escoge una publicación',
  'w.ap.noneHere': 'Nadie en este grupo.',
  'w.ap.noJobs': 'Publica un trabajo y los solicitantes llegan aquí.',
  'w.ap.note': 'Lo que escribió',
  'w.ap.move': 'Mover a',
  'w.ap.applied': 'Aplicó',

  'w.pay.hour': 'por hora',
  'w.pay.salary': 'al año',
  'w.pay.day': 'por día',
  'w.pay.contract': 'por contrato',
  'w.pay.tipped': 'más propinas',

  'w.arr.full_time': 'Tiempo completo',
  'w.arr.part_time': 'Tiempo parcial',
  'w.arr.temporary': 'Temporero',
  'w.arr.seasonal': 'Por temporada',
  // The island's own word for exactly this kind of work. `trabajo temporal` is
  // what a translator produces and it means something else.
  'w.arr.gig': 'Chiripa',

  'w.dash.heading': 'Vamos a armar tu equipo',
  'w.dash.post': 'Publicar un puesto',
  'w.dash.activeJobs': 'Puestos activos',
  'w.dash.newApplicants': 'Solicitantes nuevos',
  'w.dash.unread': 'Mensajes sin leer',
  'w.dash.openJobs': 'Tus puestos abiertos',
  'w.dash.viewAllJobs': 'Ver todos',
  'w.dash.viewApplicants': 'Ver solicitantes',
  'w.dash.applicants': 'solicitantes',
  'w.dash.recent': 'Solicitantes recientes',
  'w.dash.active': 'Activo',
  'w.dash.new': 'Nuevo',
  'w.dash.posted': 'Publicado',
  'w.dash.notPosted': 'Todavía sin publicar',
  'w.feed.greeting.morning': 'Buenos días',
  'w.feed.greeting.afternoon': 'Buenas tardes',
  'w.feed.greeting.evening': 'Buenas noches',
  'w.feed.heading': 'Encuentra trabajo en Vieques',
  'w.feed.search': 'Busca trabajos o destrezas',
  'w.feed.match': 'Buen match para ti',
  'w.feed.why.category': 'El tipo de trabajo que escogiste',
  'w.feed.why.arrangement': 'El horario que buscas',
  'w.feed.why.pay': 'En tu rango de paga',
  'w.feed.noProfile': 'Llena tu perfil y te señalamos los trabajos que te sirven.',
  'w.feed.viewJob': 'Ver puesto',
  'w.feed.more': 'Más',
  'w.jobs.interested': '¿Te interesa este trabajo?',
  'w.nav.jobs': 'Trabajos',
  'w.nav.services': 'Servicios',
  'w.nav.activity': 'Mi actividad',
  'w.nav.main': 'Principal',
  'w.nav.betaPreview': 'Vista previa beta',
  'w.footer.tagline': 'Oportunidades locales. Conexiones de la isla.',
  'w.update.title': 'Actualización requerida',
  'w.update.body': 'Esta versión de Vieques Worx está desactualizada y ya no puede funcionar. Actualiza la app y ábrela de nuevo.',
  'w.update.appStore': 'Abrir el App Store',
  'w.splash.getOnPlay': 'Descarga Vieques Worx en Google Play',
  'w.update.testFlight': 'Abrir TestFlight',
  'w.pro.title': '¿Necesitas un profesional?',
  'w.pro.hint': 'Plomeros, electricistas, choferes y más, de gente de la isla.',
  'w.pro.open': 'Buscar un profesional',
  'w.pro.back': 'Volver a mi cuenta',
  'w.tr.fromSpanish': 'Traducido del español',
  'w.tr.fromEnglish': 'Traducido del inglés',
  'w.tr.showOriginal': 'Ver el original',
  'w.tr.showTranslation': 'Ver la traducción',
  'w.role.addTitle': 'Usa esta cuenta de otra manera',
  'w.role.addHint': 'La mayoría necesita una sola. Si también haces lo demás, agrégalo aquí y aparecerá un cambio arriba.',
  'w.role.add.worker': 'También buscar trabajo',
  'w.role.add.employer': 'También emplear gente',
  'w.role.add.pro': 'También buscar u ofrecer un profesional',
  'w.role.pick': '¿Qué te trae por aquí?',
  'w.switch.working': 'Buscando trabajo',
  'w.switch.hiring': 'Empleando',
  'w.switch.toHiring': 'Cambiar a emplear',
  'w.switch.toWorking': 'Cambiar a buscar',
  'w.switch.splash': 'Portada',
  'w.switch.back': 'Volver a la app',
  'w.feed.subtitle': 'Encuentra trabajo cerca de casa.',
  'w.feed.newToday': 'trabajos nuevos hoy',
  'w.feed.newTodayOne': 'trabajo nuevo hoy',
  'w.feed.recommended': 'Recomendados para ti',
  'w.feed.seeAll': 'Ver todos',
  'w.feed.allJobs': 'Todos los trabajos en Vieques',
  'w.feed.allShort': 'Todos',
  'w.feed.done': 'Listo',
  'w.feed.posted': 'Publicado',
  'w.tab.home': 'Inicio',
  'w.admin.section': 'Administración',
  'w.bell.label': 'Avisos',
  'w.biz.tagline': 'Tu línea',
  'w.biz.taglineHint': 'Sale en cada puesto que publiques. Una línea corta.',

  'w.delete.open': 'Borrar tu cuenta',
  'w.delete.title': 'Borrar tu cuenta',
  'w.delete.what': 'Tu cuenta, tus passkeys, tu perfil, tus solicitudes, tus mensajes y tus trabajos guardados. Todo, permanentemente.',
  'w.delete.orphanWarning': 'Eres la única persona en estos negocios:',
  'w.delete.livePostings': 'puesto(s) publicado(s)',
  'w.delete.orphanOutcome': 'Sus puestos publicados se van a pausar y se le va a pedir a un administrador que le entregue el negocio a alguien que trabaje ahí.',
  'w.delete.confirmLabel': 'Escribe tu nombre de usuario para confirmar',
  'w.delete.confirm': 'Borrar mi cuenta permanentemente',
  'w.delete.permanent': 'No hay deshacer ni período de gracia.',

  'w.transport.own': 'Tengo transporte propio',
  'w.transport.shared': 'Casi siempre consigo pon',
  'w.transport.public': 'Público o caminando',
  'w.transport.none': 'El transporte es un problema',

  'w.transport.short.own': 'Propio',
  'w.transport.short.shared': 'Consigo pon',
  'w.transport.short.public': 'Público o a pie',
  'w.transport.short.none': 'Es un problema',

  'w.profile.aboutYou': 'Sobre ti',
  'w.profile.theWork': 'El trabajo que buscas',
  'w.profile.practical': 'Lo práctico',
  'w.profile.payFrom': 'Desde',
  'w.profile.payTo': 'Hasta',
  'w.profile.payPer': 'Por',
  'w.profile.categoriesHint': 'Escoge todo lo que tomarías — esto es lo que te conecta con un trabajo.',
  "w.biz.phone": "Teléfono",
  "w.biz.email": "Correo electrónico",
  "w.biz.taglineExample": "Buena comida. Días mejores.",
  "w.post.pay": "Paga",
  "w.ap.applicant": "Solicitante",
  "w.ap.applicants": "Solicitantes",
  "w.unread": "sin leer",
  "w.error.save": "No se pudo guardar.",
  "w.error.send": "No se pudo enviar.",
  "w.error.work": "No se pudo completar.",
  "w.error.jobLoad": "No se pudo cargar ese anuncio.",
  "w.error.conversationLoad": "No se pudo cargar esa conversación.",
  "w.demo.title": "Datos de demostración",
  "w.demo.practice": "Estos trabajos son ejemplos, no vacantes reales. Las solicitudes y los mensajes son solo para pruebas.",
  "w.demo.history": "Este negocio y anuncio son ejemplos, no una vacante real. Se conserva el historial de tus solicitudes.",
  "w.demo.business": "Negocio de ejemplo",
  "w.demo.badge": "Demo",
  "w.demo.apply": "Probar una solicitud de demostración",
}

/** The Worx string table. Same locale as core's `t()`, separate keys. */
export function tw(key: WorxKey): string {
  return getLocale() === 'es-PR' ? ES[key] : EN[key]
}

/**
 * Morning, afternoon or evening — where the BUSINESS is, not where the phone
 * is. Somebody checking the app from the mainland at 1am should still be told
 * what time it is on the island the work is on.
 */
export function greeting(): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'America/Puerto_Rico' })
      .format(new Date()),
  )
  if (hour < 12) return tw('w.feed.greeting.morning')
  if (hour < 18) return tw('w.feed.greeting.afternoon')
  return tw('w.feed.greeting.evening')
}

/**
 * The name to greet somebody by.
 *
 * First name only, which is what the designs show and what a person would
 * actually say — and it is also what fits: the greeting is centred on the bar
 * between the mark and the controls, and "Good morning, Marisol Rivera" runs
 * into the flags on a phone.
 */
export function firstName(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? displayName
}

/** A category's full name in the reader's language. For pickers and detail. */
export function categoryName(c: { en: string; es: string }): string {
  return getLocale() === 'es-PR' ? c.es : c.en
}

/**
 * "2 days ago", in the reader's language.
 *
 * Hours for anything inside a day — "2 hours ago" is what a list of fresh
 * postings and fresh applications both want, and "today" is vaguer than the
 * app needs to be. Intl does the words, so this never accumulates a table of
 * them per language.
 */
export function ago(at: number): string {
  const hours = Math.round((at - Date.now()) / 3_600_000)
  const rtf = new Intl.RelativeTimeFormat(getLocale() === 'es-PR' ? 'es' : 'en', { numeric: 'auto' })
  return hours > -24 ? rtf.format(hours, 'hour') : rtf.format(Math.round(hours / 24), 'day')
}

/**
 * The short name, for a filter chip.
 *
 * "Hospitality" rather than "Hospitality & Lodging" — the extra word earns its
 * place in a picker and costs a filter row its legibility on a phone.
 */
export function categoryShort(c: { shortEn: string; shortEs: string }): string {
  return getLocale() === 'es-PR' ? c.shortEs : c.shortEn
}

/**
 * Pay, as a sentence.
 *
 * "Pay not stated" is a real answer and appears in the feed rather than being
 * hidden — a posting with no pay should look different from one with pay, or
 * the absence is something the reader has to notice for themselves.
 */
export function payLabel(
  job: { payMin: number | null; payMax: number | null; payUnit: string | null },
): string {
  const unit = job.payUnit ? tw(`w.pay.${job.payUnit}` as WorxKey) : ''
  if (job.payMin === null && job.payMax === null) {
    // Tipped work with no figure still says something useful.
    return job.payUnit === 'tipped' ? tw('w.pay.tipped') : tw('w.jobs.noPay')
  }
  const money = (n: number) => `$${n.toLocaleString()}`
  const range =
    job.payMin !== null && job.payMax !== null && job.payMin !== job.payMax
      ? `${money(job.payMin)}–${money(job.payMax)}`
      : money((job.payMin ?? job.payMax)!)
  return `${range} ${unit}`.trim()
}

/**
 * The same pay, short enough for a card.
 *
 * "$14–$16/hr" rather than "$14–$16 per hour": on a card the figure has half a
 * row, and the long form wrapped onto two lines in English and three in
 * Spanish. The posting itself still says it in words — that is the screen
 * where somebody is reading rather than scanning.
 */
export function payShort(
  job: { payMin: number | null; payMax: number | null; payUnit: string | null },
): string {
  if (job.payMin === null && job.payMax === null) {
    return job.payUnit === 'tipped' ? tw('w.pay.tipped') : tw('w.jobs.noPay')
  }
  const money = (n: number) => `$${n.toLocaleString()}`
  const range =
    job.payMin !== null && job.payMax !== null && job.payMin !== job.payMax
      ? `${money(job.payMin)}–${money(job.payMax)}`
      : money((job.payMin ?? job.payMax)!)
  // A suffix, so it sits tight against the figure — except the tipped one,
  // which is "+tips" and is a separate fact rather than a unit.
  const unit = job.payUnit ? tw(`w.payShort.${job.payUnit}` as WorxKey) : ''
  return job.payUnit === 'tipped' ? `${range} ${unit}` : `${range}${unit}`
}
